import { Button } from "./ui/button";
import { useState } from "react";
import axios from "axios";
import { Check, CalendarClock, ListPlus, Trash2 } from "lucide-react";
import { API } from "@/lib/api";
import TaakVoorstel from "./TaakVoorstel";
import PostbusVerzoek from "./PostbusVerzoek";
import SkillVoorstel from "./SkillVoorstel";
import ModelAdvies from "./ModelAdvies";
import WachtrondeBlok from "./WachtrondeBlok";
import GeheugenVoorstel from "./GeheugenVoorstel";
import GeheugenGroep from "./GeheugenGroep";
import { useTaal } from "@/contexts/TaalContext";
import { kopVan, deadlineOpGlas } from "@/lib/postbusStromen";
import { zinVan, redenVan, mapVan } from "@/lib/vaultorde";
import WorkerUitslag from "./WorkerUitslag";

/** Een bericht in de postbus: wat een geplande taak opleverde. Hermes F.
 *
 *  ELKE BRON STAAT ER, MET ZIJN UITKOMST BIJ NAAM. Gelezen, leeg, niet
 *  gekoppeld of niet bereikbaar: vier verschillende dingen, en een bron die
 *  stil wegvalt mag er niet uitzien als een rustige ochtend.
 *
 *  BIJ ELKE TAAK STAAN TWEE MOGELIJKHEDEN. Een klik maakt een VOORSTEL; pas
 *  Bevestig op de kaart die dan verschijnt, doet iets in TASQ.
 */
const OP_GLAS = {
  geen_koppeling: "Niet gekoppeld",
  storing: "Kon er niet bij",
  // F2j: gekozen bron waarvan de lezer nog komt (Nextcloud-agenda, F2e).
  nog_niet: "Nog niet beschikbaar",
};

function Uitkomst({ bron }) {
  const { t } = useTaal();
  if (bron.uitkomst === "gelezen") return null;
  return (
    <p className="text-xs text-[hsl(var(--destructive))]" data-testid="postbus-bron-uitkomst">
      {OP_GLAS[bron.uitkomst] ? t(OP_GLAS[bron.uitkomst]) : bron.uitkomst}{bron.melding ? `: ${bron.melding}` : ""}
    </p>
  );
}

function TaakRij({ taak, open, onVoorstel }) {
  const { t, taal } = useTaal();
  return (
    <li className="flex flex-wrap items-center gap-2 py-1" data-testid="postbus-taak">
      <span className="min-w-0 flex-1 text-sm">
        <span className="font-medium">{taak.titel}</span>
        <span className="text-xs text-[hsl(var(--muted-foreground))]"> · {taak.werkruimte} · <span data-testid="postbus-deadline">{deadlineOpGlas(taak, taal)}</span></span>
      </span>
      <Button size="sm" variant="outline" className="min-h-9" disabled={open}
              onClick={() => onVoorstel(taak.id, "tasq_status_wijzigen")}
              data-testid="postbus-afvinken">
        <Check className="h-3 w-3 mr-1" /> {t("Afvinken")}
      </Button>
      <Button size="sm" variant="outline" className="min-h-9" disabled={open}
              onClick={() => onVoorstel(taak.id, "tasq_deadline_verzetten")}
              data-testid="postbus-verzetten">
        <CalendarClock className="h-3 w-3 mr-1" /> {t("Naar volgende werkdag")}
      </Button>
    </li>
  );
}

function TasqBron({ bron, openTaken, onVoorstel }) {
  const { t } = useTaal();
  const leeg = !bron.vandaag?.length && !bron.achterstand?.length;
  return (
    <div className="space-y-1">
      {bron.uitkomst === "gelezen" && leeg && (
        <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="postbus-tasq-leeg">
          {t("Geen deadlines vandaag en geen achterstand.")}
        </p>
      )}
      {[["achterstand", "Achterstand"], ["vandaag", "Vandaag"]].map(([sleutel, kop]) =>
        bron[sleutel]?.length ? (
          <div key={sleutel} data-testid={`postbus-tasq-${sleutel}`}>
            <p className="text-xs font-medium mt-1">{t(kop)} ({bron[sleutel].length})</p>
            <ul>
              {bron[sleutel].map((t) => (
                <TaakRij key={t.id} taak={t} open={openTaken.has(t.id)} onVoorstel={onVoorstel} />
              ))}
            </ul>
          </div>
        ) : null)}
    </div>
  );
}

/** POST E (10d): een Fathom-actiepunt opent de BESTAANDE TASQ-voorstelkaart.
 *  Er komt geen nieuwe schrijfweg bij: de kaart kiest de werkruimte, toont het
 *  voorstel en maakt alleen aan met het token van glas. */
function Actiepunt({ berichtId, punt }) {
  const { t } = useTaal();
  const [zin, setZin] = useState("");
  const [fout, setFout] = useState("");
  const open = async () => {
    try {
      const r = await axios.post(`${API}/postbus/${berichtId}/actiepunt`, { actiepunt_id: punt.id });
      setZin(r.data.zin);
    } catch (err) {
      setFout(err?.response?.data?.detail || t("Het voorstel kon niet worden geopend."));
    }
  };
  return (
    <li className="py-1 space-y-1" data-testid="postbus-actiepunt">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 text-sm">
          {punt.tekst}
          <span className="text-xs text-[hsl(var(--muted-foreground))]">
            {punt.toegewezen ? ` · ${punt.toegewezen}` : ""} · {punt.gesprek}
          </span>
        </span>
        {!zin && (
          <Button size="sm" variant="outline" className="min-h-9" onClick={open} data-testid="postbus-actiepunt-taak">
            <ListPlus className="h-3 w-3 mr-1" /> {t("Maak taakvoorstel")}
          </Button>
        )}
      </div>
      {fout && <p className="text-xs text-[hsl(var(--destructive))]">{fout}</p>}
      {zin && <TaakVoorstel keuze zin={zin} />}
    </li>
  );
}

function FathomBron({ bron, berichtId }) {
  const { t } = useTaal();
  if (bron.uitkomst !== "gelezen") return null;
  if (!bron.actiepunten?.length) {
    return <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="postbus-fathom-leeg">{t("Geen open actiepunten sinds de vorige werkdag.")}</p>;
  }
  return (
    <ul data-testid="postbus-fathom">
      {bron.actiepunten.map((p) => <Actiepunt key={p.id} berichtId={berichtId} punt={p} />)}
    </ul>
  );
}

function AgendaBron({ bron }) {
  const { t } = useTaal();
  if (bron.uitkomst !== "gelezen") return null;
  if (!bron.afspraken?.length) {
    return <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="postbus-agenda-leeg">{t("Geen afspraken vandaag.")}</p>;
  }
  return (
    <ul className="text-sm" data-testid="postbus-agenda">
      {bron.afspraken.map((a, i) => (
        <li key={i} className={a.afgezegd ? "line-through text-[hsl(var(--muted-foreground))]" : ""}>
          <span className="text-xs tabular-nums mr-2">{a.begin_op_glas}</span>{a.titel}
          {a.afgezegd ? " (afgezegd)" : ""}
        </li>
      ))}
    </ul>
  );
}

const ZICHTBAAR = 30;

/** Brain 1-op-1: wat de sync met de vault deed, met de bestanden of redenen eronder. Elke reden
 *  staat erbij (Frank: "wat niet kan, staat met reden in de Postbus"); losse regels tot ZICHTBAAR. */
function VaultBlok({ vault, t }) {
  const zin = zinVan(vault);
  const regels = vault.regels || [];
  const groepen = vault.groepen || [];
  return (
    <section className="space-y-1" data-testid="postbus-vault">
      {zin && <p className="text-sm">{t(zin[0], zin[1])}</p>}
      {regels.length > 0 && (
        <ul className="text-xs list-disc pl-4" data-testid="postbus-vault-regels">
          {regels.slice(0, ZICHTBAAR).map((r) => <li key={r}>{r}</li>)}
          {regels.length > ZICHTBAAR && <li>{t("en nog {n}", { n: regels.length - ZICHTBAAR })}</li>}
        </ul>
      )}
      {groepen.length > 0 && (
        <ul className="text-xs space-y-1" data-testid="postbus-vault-groepen">
          {vault.overgeslagen?.aantal > 0 && (
            <li className="text-[hsl(var(--muted-foreground))]" data-testid="postbus-vault-overgeslagen">
              {t("Overgeslagen mappen, niet meegeteld: {aantal} bestanden in {mappen}",
                { aantal: vault.overgeslagen.aantal, mappen: (vault.overgeslagen.mappen || []).join(", ") })}
            </li>
          )}
          {groepen.map((g) => {
            const [sleutel, waarden] = redenVan(g.reden);
            const [mapSleutel, mapWaarden] = mapVan(g.map);
            return (
              <li key={`${g.map}|${g.reden}`}>
                {t("{map}: {aantal} keer {reden}", { map: t(mapSleutel, mapWaarden), aantal: g.aantal, reden: t(sleutel, waarden) })}
                {(g.voorbeelden || []).length > 0 && (
                  <span className="block text-[hsl(var(--muted-foreground))] break-all">{g.voorbeelden.join(", ")}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default function PostbusBericht({ bericht, onVoorstel, onBesloten, gekozen = false, onKies, onVerwijder }) {
  const { t, taal } = useTaal();
  const inhoud = bericht.inhoud || {};
  const openTaken = new Set((bericht.verzoeken || []).filter((v) => !v.besloten).map((v) => v.taak_id));
  return (
    <article className="border rounded-lg p-3 space-y-3" data-testid="postbus-bericht">
      <header className="flex items-center gap-2">
        {/* B5: de kop in de taal van de app, niet in die van het moment van opslaan. */}
        <h2 className="text-sm font-medium min-w-0 flex-1" data-testid="postbus-kop">{kopVan(bericht, t, taal)}</h2>
        {!bericht.gelezen && (
          <span className="text-[10px] rounded-full border px-1.5" data-testid="postbus-nieuw">{t("nieuw")}</span>
        )}
        {onVerwijder && (
          <Button size="icon" variant="ghost" className="tap-target h-8 w-8 shrink-0" onClick={onVerwijder}
                  aria-label={t("Verwijderen")} title={t("Verwijderen")} data-testid="postbus-verwijder">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </header>
      {inhoud.mislukt && (
        <p className="text-xs text-[hsl(var(--destructive))]" data-testid="postbus-mislukt">
          {t("De ronde gaf het op na vijf pogingen: {fout}", { fout: inhoud.mislukt })}
        </p>
      )}
      {/* DW3 (24 okt): de uitslag van een worker op zijn vaste tijdstip, met het gesprek erbij. */}
      {inhoud.worker && inhoud.worker.antwoord && (
        <section className="space-y-1" data-testid="postbus-worker">
          {/* Fix 4 (5 nov): het bronblok alleen achter Laat je werk zien. */}
          <WorkerUitslag antwoord={inhoud.worker.antwoord} />
          {inhoud.worker.gesprek_id && (
            <a href={`/chat/${inhoud.worker.gesprek_id}`} className="text-xs underline" data-testid="postbus-worker-gesprek">
              {t("Open het gesprek van deze rit")}
            </a>
          )}
        </section>
      )}
      {inhoud.kennisbank && (
        <p className="text-sm" data-testid="postbus-kennisbank">
          {t("De map {map} in je vault is nu een kennisbank. Hij wordt bij deze synchronisatie ingelezen en staat onder Kennisbanken.", { map: inhoud.kennisbank.map })}
          {inhoud.kennisbank.project && ` ${t("Hij hangt aan het project {project}.", { project: inhoud.kennisbank.project })}`}
        </p>
      )}
      {inhoud.vault && <VaultBlok vault={inhoud.vault} t={t} />}
      {inhoud.wachtronde && <WachtrondeBlok ronde={inhoud.wachtronde} />}
      {inhoud.onleesbaar && (
        <p className="text-xs text-[hsl(var(--destructive))]" data-testid="postbus-onleesbaar">
          {t("Dit bericht is onleesbaar opgeslagen.")}
        </p>
      )}
      {(inhoud.bronnen || []).map((bron) => (
        <section key={bron.bron} className="space-y-1" data-testid="postbus-bron">
          <h3 className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{bron.bron}</h3>
          <Uitkomst bron={bron} />
          {"vandaag" in bron ? (
            <TasqBron bron={bron} openTaken={openTaken} onVoorstel={(id, soort) => onVoorstel(bericht.id, id, soort)} />
          ) : "actiepunten" in bron ? (
            <FathomBron bron={bron} berichtId={bericht.id} />
          ) : (
            <AgendaBron bron={bron} />
          )}
        </section>
      ))}
      {inhoud.modeladvies && <ModelAdvies advies={inhoud.modeladvies} />}
      {bericht.skill && <SkillVoorstel voorstel={bericht.skill} onBesloten={onBesloten} />}
      {bericht.geheugen && <GeheugenVoorstel voorstel={bericht.geheugen} onBesloten={onBesloten} />}
      {bericht.geheugen_groep && (
        <GeheugenGroep groep={bericht.geheugen_groep} gekozen={gekozen} onKies={onKies} onBesloten={onBesloten} />
      )}
      {(bericht.verzoeken || []).map((v) => (
        <PostbusVerzoek key={v.id} verzoek={v} onBesloten={onBesloten} />
      ))}
    </article>
  );
}
